import {Injectable} from '@angular/core';
import {HttpService} from '@app/core/services/http.service';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {UserService} from '@app/modules/shared/services/user.service';
import {DepartmentService} from '@app/modules/shared/services/department.service';
import {User} from '@app/core/models/user.model';
import {Pagination, PaginationHolder} from '@app/core/models/pagination.model';
import {DayNote} from '@app/core/models/day-note.model';
import {Department} from '@app/core/models/department.model';
import {Tenant} from '@app/core/models/tenant.model';
import {BaseService} from '@app/core/services/base.service';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {getCurrentState} from '@app/core/helpers';

@Injectable({
	providedIn: 'root',
})
export class PunchClockService extends BaseService {

	constructor(
		public httpService: HttpService,
		public userService: UserService,
		private departmentService: DepartmentService,
		public authService: AuthService,
		public interService: InternationalizationService,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public getPunchUsers(pageSize: number, page: number, inSchedule: boolean, searchTerm: string): Observable<PaginationHolder<User>> {
		const paramData = {
			page_size: pageSize,
			page,
			include: 'groups',
			append: 'pin_code_is_set',
		};

		if (inSchedule) {
			paramData['filter[has_shift]'] = inSchedule ? 1 : 0;
		}
		if (searchTerm) {
			paramData['filter[full_name]'] = searchTerm;
		}

		return this.httpService.getJson<PaginationHolder<User>>('punchUser/users', paramData).pipe(map((res: PaginationHolder<User>) => {
			res.data = res.data.map(u => {
				u.groups = u.groups.filter(g => g.department_id === getCurrentState(this.store).punchClock.department.id);

				return this.userService.fresh(u);
			});
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public punchInUser(userId: number, groupId: number, pinCode: number): Observable<User> {
		return this.httpService.postJson<User>('punchUser/users/' + userId + '/punchStart', {
			group_id: groupId,
			pin_code: pinCode,
		}).pipe(map((user: User) => {
			user.groups = user.groups.filter(g => g.department_id === getCurrentState(this.store).punchClock?.department?.id);

			return this.userService.fresh(user);
		}));
	}

	public punchOutUser(userId: number, punchNote: string, pinCode: number): Observable<User> {
		return this.httpService.postJson<User>('punchUser/users/' + userId + '/punchEnd', {
			punch_note: punchNote,
			pin_code: pinCode,
		}).pipe(map((user: User) => {
			user.groups = user.groups.filter(g => g.department_id === getCurrentState(this.store).punchClock?.department?.id);

			return this.userService.fresh(user);
		}));
	}

	public getDayNotes(page_size: number, page: number, date: string): Observable<PaginationHolder<DayNote>> {
		return this.httpService.getJson<PaginationHolder<DayNote>>('punchUser/dayNotes', {page_size, page, 'filter[covers_date]': date}).pipe(map((res: PaginationHolder<DayNote>) => {
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public getPunchDepartment(id: number): Observable<Department> {
		return this.httpService.getJson<Department>('punchUser/departments/' + id).pipe(map((res: Department) => this.departmentService.fresh(res)));
	}

	public setPunchPinCode(id: number, data: any): Observable<any> {
		return this.httpService.patchJson<any>('punchUser/users/' + id + '/setPinCode', data).pipe(map((user: User) => {
			user.groups = user.groups.filter(g => g.department_id === getCurrentState(this.store).punchClock?.department?.id);

			return this.userService.fresh(user);
		}));
	}

	public getPunchTenant(id: number): Observable<Tenant> {
		return this.httpService.getJson<Tenant>('punchUser/tenants/' + id);
	}
}
